import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import ivrService from '../../services/ivrService';
import { useT, useLocale } from '../../i18n/LanguageContext';
import { requestErrorMessage } from '../../utils/requestError';
import {
  reviewReasonNotes,
  ivrRejectionGuidance,
  ivrFollowUpNote,
} from '../../utils/reviewReasons';
import { ivrOutcome } from '../../utils/ivrOutcome';
import { IVR_STATUS_CONFIG as statusConfig, decisionDate } from './ivrPresentation';
import { isActionableIvr, isProcessedIvr } from './ivrPolicy';
import useAccessibleDialog from '../../hooks/useAccessibleDialog';
import { formatDeliveryDate } from '../../utils/orderDates';
import { submitterLabel } from '../../utils/submitterName';

import { SignedDoc, ApprovalDocumentSection } from './IvrDocumentControls';

// canAdminister controls the resolve actions shown in the modal. The page passes the viewer's
// actual role, and the backend separately enforces admin authorization for every update.
export default function IVRDetailsModal({
  record,
  onClose,
  onAction,
  canAdminister = true,
  adminDistributorId,
}) {
  const t = useT();
  const locale = useLocale();
  const [approvalStep, setApprovalStep] = useState('initial'); // 'initial' | 'uploading_covered' | 'uploading_not_covered' | 'confirmed'
  const [actionLoading, setActionLoading] = useState(false);
  const requestClose = () => {
    if (!actionLoading) onClose();
  };
  const { dialogRef, titleId } = useAccessibleDialog({
    open: !!record,
    onClose: requestClose,
    closeDisabled: actionLoading,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const recordId = record?._id;
  const recordIsProcessed = isProcessedIvr(record);

  useEffect(() => {
    if (recordId) {
      setApprovalStep(recordIsProcessed ? 'confirmed' : 'initial');
      setSelectedFile(null);
      setAdminNote('');
      setActionLoading(false);
    }
  }, [recordId, recordIsProcessed]);

  if (!record) return null;

  const firstName = record.patient?.firstName || '';
  const lastName = record.patient?.lastName || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const name = `${firstName} ${lastName}`.trim();
  const outcome = ivrOutcome(record);
  const status = statusConfig[outcome] || statusConfig.submitted;
  // Rendered for ANY record that carries reasons, not just needs_review: once an admin resolves one, the
  // reasons stay on the record and are exactly the context for WHY they decided what they decided.
  const reasonNotes = reviewReasonNotes(record.reviewReasons);
  // pVerify's "Additional Info / Additional Payer Info" free-text block (the whitelisted
  // pverify.additionalInfo string), replacing the previous Coverage table. Rendered VERBATIM: pVerify comma-joins its segments and a payer name may
  // itself contain a comma ("THE HARTFORD-CMS WC" today), so splitting on "," can corrupt a value.
  // Shown to whoever can already read this record; tenancy is unchanged.
  const additionalInfo = record.pverify?.additionalInfo || '';
  const isProcessed = approvalStep === 'confirmed' || isProcessedIvr(record);
  const decidedOn = decisionDate(record, locale);
  const isActionable = isActionableIvr(record);
  const followUp = ivrFollowUpNote(record.followUp, locale, outcome);
  const reviewPreconditions = {
    expectedStatus: record.status,
    expectedUpdatedAt: record.updatedAt,
  };

  const handleReviewError = (err, fallback, decisionStarted = true) => {
    if (decisionStarted && err?.response?.status === 409) {
      alert(
        t(
          'This IVR changed or needs to be reloaded before review. Your decision was not saved. Reloading the list.'
        )
      );
      onAction();
      onClose();
      return;
    }
    if (decisionStarted && err?.response?.status === 404) {
      alert(t('This IVR is no longer available. Reloading the list.'));
      onAction();
      onClose();
      return;
    }
    alert(t(requestErrorMessage(err, fallback)));
  };

  const handleReject = async () => {
    if (!adminNote.trim()) {
      alert(t('Please enter a note explaining why this IVR is being rejected.'));
      return;
    }
    setActionLoading(true);
    try {
      const rejectPayload = { status: 'rejected', note: adminNote.trim(), ...reviewPreconditions };
      await ivrService.updateIVR(record._id, rejectPayload);
      onAction();
      onClose();
    } catch (err) {
      handleReviewError(err, 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDocumentConfirm = async (targetStatus) => {
    setActionLoading(true);
    let decisionStarted = false;
    try {
      let approvalDocument;
      // Upload the file first if one is selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        approvalDocument = uploadRes.data?.data?.url || uploadRes.data?.url;
      }
      const payload = { status: targetStatus, ...reviewPreconditions };
      if (approvalDocument) payload.approvalDocument = approvalDocument;
      if (adminNote.trim()) payload.note = adminNote.trim();
      decisionStarted = true;
      await ivrService.updateIVR(record._id, payload);
      setApprovalStep('confirmed');
      onAction();
      onClose();
    } catch (err) {
      handleReviewError(err, 'Failed to update', decisionStarted);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={requestClose} />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative bg-white rounded-[14px] shadow-xl w-[500px] max-w-[calc(100vw-2rem)] z-10 max-h-[90vh] overflow-y-auto"
      >
        <div className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px] px-5 py-4 flex items-center justify-between">
          <h2 id={titleId} className="text-[16px] font-semibold text-[#0f172a]">
            {t('IVR Details')}
          </h2>
          <button
            type="button"
            onClick={requestClose}
            disabled={actionLoading}
            aria-label={t('Close')}
            className="w-6 h-6 flex items-center justify-center text-[#64748b] hover:text-[#0f172a]"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[24px]">
              cancel
            </span>
          </button>
        </div>

        <div className="p-5">
          <div className="bg-[#f9fafc] rounded-[13px] p-4 mb-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-[34px] h-[34px] rounded-full bg-[#e6f1ff] flex items-center justify-center text-[13px] font-semibold text-[#0f172a]">
                {initials}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-[13px] font-semibold text-[#0f172a]">{name}</p>
                  <span
                    className={`inline-flex items-center px-2 py-0 rounded-[14px] text-[10px] font-medium ${status.bg} ${status.text}`}
                  >
                    {t(status.label)}
                  </span>
                </div>
                <p className="text-[11px] font-medium text-[#64748b]">{record.requestId}</p>
              </div>
            </div>

            {/*
              WHY this request needs a human.
              The classifier already worked this out and persisted it; until now no client read it, so the
              admin saw "Needs Review" and had to guess whether it was a Medicare Advantage plan, an
              unconfirmed Part B, a hospice election or a payer error. That is exactly the information
              needed to resolve the record, and it was being thrown away at the last step.
            */}
            {reasonNotes.length > 0 && (
              <div className="mb-3" data-testid="review-reasons">
                {/*
                  The heading has to follow the STATUS, not just the presence of reasons. A record can be Covered and still carry notes - a QMB dual-eligible, a
                  demographic hint - because those govern how we bill rather than whether Medicare
                  pays. Calling them "why this needs review" on a record that needs no review would
                  send an admin looking for a decision that has already been made.
                */}
                <p className="text-[11px] font-medium text-[#64748b] mb-1.5">
                  {outcome === 'needs_review'
                    ? t('Why this needs review')
                    : outcome === 'rejected'
                      ? t('Why this was rejected')
                      : outcome === 'not_covered'
                        ? t('Why this is not covered')
                        : t('Worth knowing')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {reasonNotes.map(({ code, label, informational }) => (
                    <span
                      key={code}
                      data-testid={informational ? 'reason-note' : 'reason-warning'}
                      className={`inline-flex items-center px-2 py-0.5 rounded-[6px] text-[11px] font-medium border ${
                        informational
                          ? 'bg-[#e6f1ff] text-[#0069c4] border-[#bfdbfe]'
                          : 'bg-[#f3e8ff] text-[#7e22ce] border-[#d8b4fe]'
                      }`}
                    >
                      {t(label)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {outcome === 'rejected' && (
              <p className="text-[12px] text-[#64748b] mb-3">{t(ivrRejectionGuidance(record))}</p>
            )}

            {followUp && (
              <p className="text-[12px] text-[#64748b] mb-3" role="status">
                {t(followUp.message, followUp.values)}
              </p>
            )}

            {additionalInfo && (
              <div className="mb-3" data-testid="additional-info">
                <p className="text-[11px] font-medium text-[#64748b] mb-1.5">
                  {t('Additional Info / Additional Payer Info')}{' '}
                  <span className="font-normal text-[#94a3b8]">
                    {t('(as reported by the payer)')}
                  </span>
                </p>
                <div className="rounded-[8px] border border-[#e2e8f0] px-2.5 py-2">
                  <p className="text-[12px] leading-[1.55] text-[#0f172a] whitespace-pre-wrap break-words">
                    {additionalInfo}
                  </p>
                </div>
              </div>
            )}

            {isProcessed && (decidedOn || record.approvalDocument) && (
              <div className="flex items-center justify-between border-t border-[#e8ebf1] pt-3">
                {decidedOn ? (
                  <p className="text-[12px] font-medium text-[#64748b]">
                    {t('Decision made on {date}', { date: decidedOn })}
                  </p>
                ) : (
                  <span />
                )}
                {record.approvalDocument && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-[6px] text-[11px] font-medium bg-[rgba(222,252,237,0.6)] text-[#007a55] border border-[#007a55]">
                    {t('Document Attached')}
                  </span>
                )}
              </div>
            )}

            {canAdminister && isActionable && approvalStep === 'initial' && (
              <>
                <div className="mt-3 mb-3">
                  <p className="text-[12px] font-medium text-[#0f172a] mb-1">
                    {t('Note from MedEffects')}{' '}
                    <span className="text-[11px] font-normal text-[#64748b]">
                      {t('(required for Reject)')}
                    </span>
                  </p>
                  <textarea
                    aria-label={t('Note from MedEffects')}
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder={t('Add a note explaining your decision...')}
                    className="w-full h-[60px] border border-[#d6dce8] rounded-[8px] px-3 py-2 text-[13px] text-[#0f172a] placeholder:text-[#97a3b6] resize-none focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={handleReject}
                    disabled={actionLoading || !adminNote.trim()}
                    title={!adminNote.trim() ? t('Enter a note above to reject') : ''}
                    className={`flex items-center gap-1.5 h-[32px] px-3 border rounded-[6px] bg-white transition-colors ${
                      adminNote.trim() && !actionLoading
                        ? 'border-[#f23e41] hover:bg-red-50 cursor-pointer'
                        : 'border-[#e2e8f0] opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <span className="material-symbols-outlined text-[#f23e41] text-[14px]">
                      close
                    </span>
                    <span className="text-[12px] font-medium text-[#f23e41]">{t('Reject')}</span>
                  </button>
                  <button
                    onClick={() => setApprovalStep('uploading_covered')}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 h-[32px] px-4 bg-[#007a55] rounded-[6px] shadow-sm hover:bg-[#006647] transition-colors"
                  >
                    <span className="material-symbols-outlined text-white text-[14px]">check</span>
                    <span className="text-[12px] font-medium text-white">{t('Covered')}</span>
                  </button>
                  <button
                    onClick={() => setApprovalStep('uploading_not_covered')}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 h-[32px] px-4 bg-[#363998] rounded-[6px] shadow-sm hover:bg-[#2a2d7a] transition-colors"
                  >
                    <span className="material-symbols-outlined text-white text-[14px]">block</span>
                    <span className="text-[12px] font-medium text-white">{t('Not Covered')}</span>
                  </button>
                </div>
              </>
            )}
          </div>

          {canAdminister && approvalStep === 'uploading_covered' && (
            <ApprovalDocumentSection
              selectedFile={selectedFile}
              onFileChange={(file) => setSelectedFile(file)}
              onRemoveFile={() => setSelectedFile(null)}
              onCancel={() => {
                setApprovalStep('initial');
                setSelectedFile(null);
              }}
              onConfirm={() => handleDocumentConfirm('covered')}
              loading={actionLoading}
              confirmLabel="Confirm Covered"
              fileOptional={true}
            />
          )}

          {canAdminister && approvalStep === 'uploading_not_covered' && (
            <ApprovalDocumentSection
              selectedFile={selectedFile}
              onFileChange={(file) => setSelectedFile(file)}
              onRemoveFile={() => setSelectedFile(null)}
              onCancel={() => {
                setApprovalStep('initial');
                setSelectedFile(null);
              }}
              onConfirm={() => handleDocumentConfirm('not_covered')}
              loading={actionLoading}
              confirmLabel="Confirm Not Covered"
              fileOptional={true}
              showNote={true}
              adminNote={adminNote}
              onNoteChange={setAdminNote}
            />
          )}

          <div className="h-px bg-[#d6dce8] mb-5" />

          {record.businessContext === 'distributor' && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mb-5 text-[12px]">
              <dt className="font-medium text-[#64748b]">{t('Customer')}</dt>
              <dd className="break-words">{record.clinic?.name || t('Not set')}</dd>
              <dt className="font-medium text-[#64748b]">{t('Submitted by')}</dt>
              <dd className="break-words">{submitterLabel(record.submittedBy, t)}</dd>
              <dt className="font-medium text-[#64748b]">{t('Date submitted')}</dt>
              <dd>
                {record.createdAt && !Number.isNaN(new Date(record.createdAt).getTime())
                  ? new Date(record.createdAt).toLocaleString(locale)
                  : t('Not set')}
              </dd>
              {record.patient?.dateOfBirth && (
                <>
                  <dt className="font-medium text-[#64748b]">{t('Date of birth')}</dt>
                  <dd>{formatDeliveryDate(record.patient.dateOfBirth, locale) || t('Not set')}</dd>
                </>
              )}
              {(record.dateOfServiceStart || record.dateOfServiceEnd) && (
                <>
                  <dt className="font-medium text-[#64748b]">{t('Service dates')}</dt>
                  <dd>
                    {formatDeliveryDate(record.dateOfServiceStart, locale) || t('Not set')} –{' '}
                    {formatDeliveryDate(record.dateOfServiceEnd, locale) || t('Not set')}
                  </dd>
                </>
              )}
            </dl>
          )}

          <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-3">
            {t('Insurance Details')}
          </p>
          <div className="grid grid-cols-1 gap-4 mb-5">
            <div>
              <p className="text-[12px] font-medium text-[#64748b] mb-1">{t('Medicare ID')}</p>
              <p className="text-[14px] font-medium text-[#0f172a]">
                {record.insurance?.medicareId || t('N/A')}
              </p>
            </div>
          </div>

          <p className="text-[12px] font-medium text-[#64748b] mb-2">{t('Patient Comments')}</p>
          <div className="w-full min-h-[60px] border border-[#d6dce8] rounded-[8px] px-4 py-3 text-[14px] text-[#24315d] mb-5">
            {record.comment || t('No comments')}
          </div>

          {/* Admin Note - read-only when NOT actionable, and ALWAYS read-only for a non-admin
              (a rep has no editable action card, so this is their only view of the note). */}
          {(!isActionable || !canAdminister) && record.adminNote ? (
            <>
              <p className="text-[12px] font-medium text-[#64748b] mb-2">
                {t('Note from MedEffects')}
              </p>
              <div className="w-full min-h-[40px] border border-[#d6dce8] rounded-[8px] px-4 py-3 text-[14px] text-[#24315d] mb-5 bg-[#f9fafc]">
                {record.adminNote}
              </div>
            </>
          ) : null}

          {record.approvalDocument && (
            <>
              <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-3">
                {t('Approval Document')}
              </p>
              <div className="flex flex-wrap gap-3 mb-5">
                {(() => {
                  const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(record.approvalDocument);
                  return (
                    <SignedDoc
                      adminDistributorId={adminDistributorId}
                      recordId={record._id}
                      docRef="approval"
                      className="flex flex-col items-center gap-1 p-2 border border-[#007a55] rounded-[8px] hover:bg-[#f0fdf4] transition-colors cursor-pointer bg-[rgba(222,252,237,0.15)]"
                      title={t('Click to open approval document')}
                    >
                      {(url) => (
                        <>
                          {isImg ? (
                            <img
                              src={url || undefined}
                              alt={t('Approval Doc')}
                              className="w-[50px] h-[50px] object-cover rounded"
                            />
                          ) : (
                            <span className="material-symbols-outlined text-[#007a55] text-[36px]">
                              verified
                            </span>
                          )}
                          <span className="text-[10px] font-medium text-[#007a55]">
                            {t('Approval Doc')}
                          </span>
                        </>
                      )}
                    </SignedDoc>
                  );
                })()}
              </div>
            </>
          )}

          {record.documents && record.documents.length > 0 && (
            <>
              <p className="text-[12px] font-semibold text-[#64748b] uppercase tracking-wider mb-3">
                {t('Attached Documents')}
              </p>
              <div className="flex flex-wrap gap-3">
                {record.documents.map((doc, i) => {
                  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc);
                  return (
                    <SignedDoc
                      adminDistributorId={adminDistributorId}
                      key={i}
                      recordId={record._id}
                      docRef={`doc:${i}`}
                      className="flex flex-col items-center gap-1 p-2 border border-[#e2e8f0] rounded-[8px] hover:bg-gray-50 transition-colors cursor-pointer"
                      title={t('Click to open')}
                    >
                      {(url) => (
                        <>
                          {isImage ? (
                            <img
                              src={url || undefined}
                              alt={t('Doc {n}', { n: i + 1 })}
                              className="w-[50px] h-[50px] object-cover rounded"
                            />
                          ) : (
                            <span className="material-symbols-outlined text-[#f23e41] text-[36px]">
                              picture_as_pdf
                            </span>
                          )}
                          <span className="text-[10px] text-[#64748b]">
                            {t('Doc {n}', { n: i + 1 })}
                          </span>
                        </>
                      )}
                    </SignedDoc>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
